/* eslint-disable react/jsx-no-bind */
/**
 * #######################################################@
 *
 * Shared select-like trigger for <FiltersPanel /> controls
 *
 * Gives the "code" (CompactSelectFilter) and "date"
 * (CompactDateFilter) filters the exact same look: an
 * outlined, rounded field showing the current selection,
 * a leading icon, an optional count badge / clear button
 * and a chevron.
 *
 * #######################################################@
 */
import { Icon, IconButton } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";

export default function FilterTrigger({
	icon = "filter_alt",
	active = false,
	open = false,
	text,
	placeholder,
	badge = null,
	onClick,
	onClear,
	darkMode
}) {
	return (
		<MDBox
			onClick={onClick}
			sx={({ palette, borders }) => ({
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				width: "100%",
				gap: 0.75,
				px: 1.25,
				py: 0.65,
				cursor: "pointer",
				userSelect: "none",
				borderRadius: borders.borderRadius.lg,
				border: `1px solid ${
					open ? palette.info.main : darkMode ? palette.grey[700] : palette.grey[300]
				}`,
				backgroundColor: darkMode ? palette.background.card : palette.white.main,
				transition: "border-color .15s ease, box-shadow .15s ease",
				boxShadow: open ? `0 0 0 2px ${palette.info.main}33` : "none",
				"&:hover": {
					borderColor: open ? palette.info.main : palette.grey[500]
				}
			})}
		>
			<MDBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
				<Icon
					fontSize="small"
					sx={{ color: active ? "info.main" : "text.secondary", flexShrink: 0 }}
				>
					{icon}
				</Icon>
				<MDTypography
					variant="button"
					fontWeight={active ? "medium" : "regular"}
					color={active ? "text" : "secondary"}
					noWrap
					sx={{ display: "block", minWidth: 0 }}
				>
					{active ? text : placeholder}
				</MDTypography>
			</MDBox>

			<MDBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
				{badge !== null && badge !== undefined && (
					<MDBox
						sx={({ palette }) => ({
							minWidth: 20,
							height: 20,
							px: 0.5,
							borderRadius: "10px",
							backgroundColor: palette.info.main,
							color: palette.white.main,
							fontSize: 11,
							fontWeight: 700,
							lineHeight: "20px",
							textAlign: "center"
						})}
					>
						{badge}
					</MDBox>
				)}
				{active && onClear && (
					<IconButton
						size="small"
						sx={{ p: 0.25 }}
						onClick={e => {
							e.stopPropagation();
							onClear();
						}}
					>
						<Icon fontSize="small" sx={{ color: "text.secondary" }}>
							close
						</Icon>
					</IconButton>
				)}
				<Icon fontSize="small" sx={{ color: "text.secondary" }}>
					{open ? "expand_less" : "expand_more"}
				</Icon>
			</MDBox>
		</MDBox>
	);
}
